import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";

export default function Registrati() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    password: "", password2: "",
    privacy_consent: false, newsletter_consent: false,
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const input = "w-full rounded-xl bg-white border border-[#292522]/12 px-4 py-3 focus:border-[#FF6100] focus:ring-2 focus:ring-[#FF6100]/20 outline-none text-sm";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy_consent) return toast.error("Devi accettare l'informativa privacy");
    if (form.password.length < 8) return toast.error("La password deve avere almeno 8 caratteri");
    if (form.password !== form.password2) return toast.error("Le password non coincidono");
    setLoading(true);
    try {
      await register({
        email: form.email.trim().toLowerCase(),
        password: form.password,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone: form.phone.trim(),
        privacy_consent: form.privacy_consent,
        newsletter_consent: form.newsletter_consent,
      });
      setDone(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore in registrazione");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="register-success" className="min-h-[70vh] flex items-center justify-center">
        <div className="container-vv max-w-xl text-center py-16">
          <div className="mx-auto h-16 w-16 rounded-full bg-[#F5E9C7] flex items-center justify-center mb-6">
            <CheckCircle2 className="h-8 w-8 text-[#B69838]" strokeWidth={1.75} />
          </div>
          <h1 className="font-display font-medium text-4xl">Controlla la tua email</h1>
          <p className="mt-4 text-[#292522]/75">
            Ti abbiamo inviato un link di conferma a <strong>{form.email}</strong>.
            Clicca il link per attivare il tuo account. Il link scade fra 48 ore.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/accedi" className="btn-primary" data-testid="go-to-login">Vai al login</Link>
            <Link to="/" className="btn-ghost">Torna alla home</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="register-page" className="min-h-[70vh]">
      <section className="bg-[#FFF1E8] py-14">
        <div className="container-vv">
          <div className="eyebrow">Area riservata</div>
          <h1 className="mt-4 font-display font-medium text-5xl sm:text-6xl tracking-tight">Registrati</h1>
          <p className="mt-4 text-lg text-[#292522]/75 max-w-2xl">
            Crea un account per prenotare eventi con priorità e restare in contatto con la comunità. Se sei già socio/a, l'amministrazione ti attiverà il ruolo appropriato dopo la registrazione.
          </p>
        </div>
      </section>
      <section className="container-vv py-14 max-w-2xl">
        <form onSubmit={submit} className="card-vv p-8 bg-white space-y-4" data-testid="register-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <label><span className="text-xs font-semibold text-[#292522]/70">Nome *</span>
              <input required className={input + " mt-1"} value={form.first_name} onChange={upd("first_name")} data-testid="reg-first-name" />
            </label>
            <label><span className="text-xs font-semibold text-[#292522]/70">Cognome *</span>
              <input required className={input + " mt-1"} value={form.last_name} onChange={upd("last_name")} data-testid="reg-last-name" />
            </label>
          </div>
          <label className="block"><span className="text-xs font-semibold text-[#292522]/70">Email *</span>
            <input required type="email" className={input + " mt-1"} value={form.email} onChange={upd("email")} data-testid="reg-email" />
          </label>
          <label className="block"><span className="text-xs font-semibold text-[#292522]/70">Telefono (opzionale)</span>
            <input className={input + " mt-1"} value={form.phone} onChange={upd("phone")} data-testid="reg-phone" placeholder="+39 …" />
          </label>
          <div className="grid sm:grid-cols-2 gap-4">
            <label><span className="text-xs font-semibold text-[#292522]/70">Password * <span className="opacity-60">(min 8 caratteri)</span></span>
              <input required type="password" minLength={8} className={input + " mt-1"} value={form.password} onChange={upd("password")} data-testid="reg-password" />
            </label>
            <label><span className="text-xs font-semibold text-[#292522]/70">Ripeti password *</span>
              <input required type="password" minLength={8} className={input + " mt-1"} value={form.password2} onChange={upd("password2")} data-testid="reg-password2" />
            </label>
          </div>
          <label className="flex items-start gap-3 text-sm mt-2">
            <input type="checkbox" checked={form.privacy_consent} onChange={upd("privacy_consent")} className="mt-1" data-testid="reg-privacy" />
            <span>Ho letto e accetto l'<Link to="/privacy" className="text-[#FF6100] underline">informativa privacy</Link> *</span>
          </label>
          <label className="flex items-start gap-3 text-sm">
            <input type="checkbox" checked={form.newsletter_consent} onChange={upd("newsletter_consent")} className="mt-1" data-testid="reg-newsletter" />
            <span>Voglio ricevere aggiornamenti e newsletter da Vola Vola APS</span>
          </label>
          <button disabled={loading} className="btn-primary w-full justify-center mt-2" data-testid="reg-submit">
            <UserPlus className="h-4 w-4" strokeWidth={1.75} />
            {loading ? "Registrazione…" : "Crea account"}
          </button>
          <p className="text-xs text-center text-[#292522]/60 mt-2">
            Hai già un account? <Link to="/accedi" className="text-[#FF6100]">Accedi</Link>
          </p>
        </form>
      </section>
    </div>
  );
}
